import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    EntData,
    EntDescription,
    EntList,
    EntSchema,
    SchemaType,
} from '@bazis/shared/models/srv.types';
import { EntStorage } from '@bazis/shared/models/storage.types';

/**
 * Default empty storage structure used to initialize `BazisStorageService`.
 * Cloned to avoid shared references between instances.
 */
const DEFAULT_STORAGE: EntStorage<EntData, EntList, EntSchema> = {
    list: {},
    entity: {},
    schema: {
        schema_list: {},
        schema_create: {},
        schema_update: {},
        schema_retrieve: {},
        schema_transit: {},
    },
};

/**
 * **BazisStorageService**
 *
 * In-memory storage/cache for entities, lists, and schemas.
 * Stores both raw values and `BehaviorSubject` containers so consumers can subscribe
 * to item/list/schema updates via Observables.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisStorageService {
    /**
     * Synchronous storage values.
     * Shape matches `DEFAULT_STORAGE`.
     */
    private storage: EntStorage<EntData, EntList, EntSchema> = JSON.parse(
        JSON.stringify(DEFAULT_STORAGE),
    );

    /**
     * Reactive storage containers (`BehaviorSubject`) for entities, lists, and schemas.
     * Each item is created lazily when requested via `getItem$()`, `getList$()`, `getSchema$()`.
     */
    private storage$: EntStorage<
        BehaviorSubject<EntData>,
        BehaviorSubject<EntList>,
        BehaviorSubject<EntSchema>
    > = JSON.parse(JSON.stringify(DEFAULT_STORAGE));

    /**
     * Get an entity synchronously from storage.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns Stored entity `EntData` or `null`/`undefined` when missing.
     */
    getItem(entityType: string, id: string = ''): EntData {
        return this._getExistedItem(this.storage, entityType, id);
    }

    /**
     * Get a schema synchronously from storage.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Optional entity id for id-specific schemas. Default: empty string.
     * @returns Stored schema `EntSchema` or `null`/`undefined` when missing.
     */
    getSchema(schemaType: SchemaType, entityType: string, id: string = ''): EntSchema {
        return this._getExistedSchema(this.storage, schemaType, entityType, id);
    }

    /**
     * Get an entity as an Observable.
     *
     * Creates internal `BehaviorSubject` container when missing.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns Observable of entity `EntData`.
     */
    getItem$(entityType: string, id: string = ''): Observable<EntData> {
        return this._getItem$(entityType, id).asObservable();
    }

    /**
     * Get a schema as an Observable.
     *
     * Creates internal `BehaviorSubject` container when missing.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Optional entity id. Default: empty string.
     * @returns Observable of schema `EntSchema`.
     */
    getSchema$(schemaType: SchemaType, entityType: string, id: string = ''): Observable<EntSchema> {
        return this._getSchema$(schemaType, entityType, id).asObservable();
    }

    /**
     * Get a parent-derived schema synchronously.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type.
     * @param parentId - Parent entity id.
     * @returns Stored schema `EntSchema` or `null`/`undefined` when missing.
     */
    getSchemaFromParent(
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
    ): EntSchema {
        return this._getExistedSchemaFromParent(
            this.storage,
            schemaType,
            entityType,
            parentType,
            parentId,
        );
    }

    /**
     * Get a parent-derived schema as an Observable.
     *
     * Creates internal `BehaviorSubject` container when missing.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type.
     * @param parentId - Parent entity id.
     * @returns Observable of schema `EntSchema`.
     */
    getSchemaFromParent$(
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
    ): Observable<EntSchema> {
        return this._getSchemaFromParent$(
            schemaType,
            entityType,
            parentType,
            parentId,
        ).asObservable();
    }

    /**
     * Get a list as an Observable.
     *
     * Creates internal `BehaviorSubject` container when missing.
     *
     * @param entityType - Entity type string.
     * @returns Observable of `EntList`.
     */
    getList$(entityType: string): Observable<EntList> {
        return this._getList$(entityType);
    }

    /**
     * Patch entity snapshot fields and notify subscribers.
     *
     * Does nothing when item is missing.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @param properties - Plain object with `$snapshot` fields to merge.
     * @returns void
     */
    patchItem(entityType: string, id: string, properties: any) {
        const root = this._getItemRoot(this.storage, 'entity');
        const path = this._getItemPath(entityType, id);
        const item = this.getItem(entityType, id);
        if (!item) return;
        const newItem = {
            ...item,
            $snapshot: {
                ...item.$snapshot,
                ...properties,
            },
        };
        root[path] = newItem;
        const item$ = this._getItem$(entityType, id);
        if (item$) item$.next(newItem);
    }

    /**
     * Merge updated entity data into existing storage item and notify subscribers.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @param updatedItem - Entity data to merge (top-level + `$snapshot`).
     * @returns void
     */
    mergeItems(entityType: string, id: string, updatedItem: EntData) {
        const root = this._getItemRoot(this.storage, 'entity');
        const path = this._getItemPath(entityType, id);
        const item = this.getItem(entityType, id);
        const newItem = {
            ...item,
            ...updatedItem,
            $snapshot: {
                ...item?.$snapshot,
                ...updatedItem.$snapshot,
            },
        };
        root[path] = newItem;
        const item$ = this._getItem$(entityType, id);
        if (item$) item$.next(newItem);
    }

    /**
     * Set an entity item into storage and notify subscribers.
     *
     * If item contains `included`, it stores included items recursively and then
     * replaces `included` with `{ type, id }` references.
     *
     * @param entityType - Entity type string.
     * @param item - Entity data to store.
     * @param id - Optional entity id override. Default: empty string.
     * @returns void
     */
    setItem(entityType: string, item, id: string = '') {
        if (item.included) {
            item.included.forEach((itemIncluded) => {
                this.getItem$(itemIncluded.type, itemIncluded.id);
                this.setItem(itemIncluded.type, itemIncluded, itemIncluded.id);
            });

            item.included = item.included.map((v) => {
                return {
                    type: v.type,
                    id: v.id,
                };
            });
        }
        const root = this._getItemRoot(this.storage, 'entity');
        const path = this._getItemPath(entityType, id);
        root[path] = item;
        const item$ = this._getItem$(entityType, id);
        if (item$) item$.next(item);
    }

    /**
     * Set schema into storage and notify subscribers.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param item - Schema object to store.
     * @param id - Optional entity id. Default: empty string.
     * @returns void
     */
    setSchema(schemaType: SchemaType, entityType: string, item, id: string = '') {
        const existedSchema$ = this._getExistedSchema$(schemaType, entityType, id);
        if (existedSchema$) existedSchema$.next(item);

        const schemaRoot$ = this._getItemRoot(this.storage$, 'schema', schemaType);
        const schemaRoot = this._getItemRoot(this.storage, 'schema', schemaType);

        const schemaPath = this._getItemPath(entityType, id);
        schemaRoot[schemaPath] = item;
        if (schemaRoot$[schemaPath]) {
            schemaRoot$[schemaPath].next(item);
        }
    }

    /**
     * Set parent-derived schema into storage and notify subscribers.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type string.
     * @param parentId - Parent entity id string.
     * @param item - Schema object to store.
     * @returns void
     */
    setSchemaFromParent(
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
        item,
    ) {
        const existedSchema$ = this._getExistedSchemaFromParent$(
            schemaType,
            entityType,
            parentType,
            parentId,
        );
        if (existedSchema$) existedSchema$.next(item);

        const schemaRoot$ = this._getItemRoot(this.storage$, 'schema', schemaType);
        const schemaRoot = this._getItemRoot(this.storage, 'schema', schemaType);

        const schemaPath = this._getItemSchemaFromParentPath(entityType, parentType, parentId);
        schemaRoot[schemaPath] = item;
        if (schemaRoot$[schemaPath]) {
            schemaRoot$[schemaPath].next(item);
        }
    }

    // перезаписываем/записываем все содержимое списка
    /**
     * Replace and store a full list for an entity type and notify subscribers.
     *
     * Also updates entity storage for each list item (by item `type` + `id`) and notifies
     * item subscribers if subjects exist.
     *
     * @param entityType - Entity type string.
     * @param list - List object (expected `EntList` shape).
     * @returns void
     */
    setList(entityType: string, list) {
        const root = this._getItemRoot(this.storage, 'list');
        const path = this._getItemPath(entityType);
        root[path] = list;
        const existedItem$ = this._getExistedList$(entityType);
        if (existedItem$) existedItem$.next(list);

        const listEnt = list;
        const entityRoot$ = this._getItemRoot(this.storage$, 'entity');
        const entityRoot = this._getItemRoot(this.storage, 'entity');
        if (!listEnt.list) return;

        listEnt.list.forEach((listItem) => {
            const listItemPath = this._getItemPath(listItem.type, listItem.id);
            entityRoot[listItemPath] = listItem;
            if (entityRoot$[listItemPath]) {
                entityRoot$[listItemPath].next(listItem);
            }
        });
    }

    //чистим все хранилище
    /**
     * Clear all storage (lists, entities, schemas).
     *
     * For entities, you can pass an exclude list to keep some cached entities.
     * All `BehaviorSubject` instances for removed items are completed.
     *
     * @param excludeEntities - List of entities to keep: `{ entityType, id }[]`. Default: [].
     * @returns void
     */
    clearStorage(excludeEntities: { entityType: string; id: string }[] = []) {
        const excludePaths = excludeEntities.map((excludeEntity) =>
            this._getItemPath(excludeEntity.entityType, excludeEntity.id),
        );

        ['list', 'entity', 'schema'].forEach((storageType) => {
            if (storageType === 'schema') {
                Object.keys(this.storage$[storageType]).forEach((schemaType) => {
                    Object.keys(this.storage$[storageType][schemaType]).forEach((path) => {
                        this.removeItemByPath(storageType, path, schemaType);
                    });
                });
            } else {
                Object.keys(this.storage$[storageType]).forEach((path) => {
                    if (storageType === 'entity' && excludePaths.indexOf(path) > -1) return;
                    this.removeItemByPath(storageType, path);
                });
            }
        });
    }

    /**
     * Remove all list and entity items that match the given entity type.
     *
     * This method iterates through existing subjects in `storage$` and removes matching paths.
     *
     * @param entityType - Entity type string to remove (matched by path substring).
     * @returns void
     */
    removeItemsByType(entityType) {
        ['list', 'entity'].forEach((storageType) => {
            Object.keys(this.storage$[storageType]).forEach((path) => {
                if (storageType === 'entity' && path.includes(entityType)) {
                    this.removeItemByPath(storageType, path);
                }
            });
        });
    }

    // удаляем сущность по типу и id
    /**
     * Remove an item from storage by type and id.
     *
     * @param storageType - Storage section: `list`, `entity`, or `schema`.
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @param schemaType - Schema type string when removing schema items. Default: empty string.
     * @returns void
     */
    removeItem(storageType: string, entityType: string, id: string = '', schemaType: string = '') {
        const path = this._getItemPath(entityType, id);
        this.removeItemByPath(storageType, path, schemaType);
    }

    /**
     * Remove an item by pre-built storage path.
     *
     * @param storageType - Storage section: `list`, `entity`, or `schema`.
     * @param path - Internal storage key path.
     * @param schemaType - Schema type string when removing schema items. Default: empty string.
     * @returns void
     */
    removeItemByPath(storageType: string, path: string = '', schemaType: string = '') {
        this._checkAndDeleteStorageItem(storageType, path, schemaType);
    }

    // существует ли уже behavior BehaviorSubject для сущности, если да,
    // предпологается, что она будет заполнена данными
    /**
     * Check whether an entity subject exists in reactive storage (`storage$`).
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns True when subject exists, otherwise false.
     */
    isExistedItem(entityType: string, id: string = ''): boolean {
        return !!this._getExistedItem(this.storage$, entityType, id);
    }

    // существует ли уже behavior BehaviorSubject для схемы, если да,
    // предпологается, что она будет заполнена данными
    /**
     * Check whether a schema subject exists in reactive storage (`storage$`).
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns True when subject exists, otherwise false.
     */
    isExistedSchema(schemaType: SchemaType, entityType: string, id: string = ''): boolean {
        return !!this._getExistedSchema(this.storage$, schemaType, entityType, id);
    }

    /**
     * Check whether a parent-derived schema subject exists in reactive storage (`storage$`).
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type.
     * @param parentId - Parent entity id.
     * @returns True when subject exists, otherwise false.
     */
    isExistedSchemaFromParent(
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
    ): boolean {
        return !!this._getExistedSchemaFromParent(
            this.storage$,
            schemaType,
            entityType,
            parentType,
            parentId,
        );
    }

    /**
     * Check whether a list subject exists in reactive storage (`storage$`).
     *
     * @param entityType - Entity type string.
     * @returns True when subject exists, otherwise false.
     */
    isExistedList(entityType: string): boolean {
        return !!this._getExistedList(this.storage$, entityType);
    }

    // Получаем сущность асинхронно
    // если сущность отсутствует создаем BehaviorSubject
    /**
     * Get or create entity subject in reactive storage.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns BehaviorSubject that holds `EntData` value.
     */
    private _getItem$(entityType: string, id: string = ''): BehaviorSubject<EntData> {
        let existedItem = this._getExistedItem$(entityType, id);
        if (existedItem) return existedItem;
        const root$ = this._getItemRoot(this.storage$, 'entity');

        const path = this._getItemPath(entityType, id);
        root$[path] = new BehaviorSubject(null);
        return this._getItem$(entityType, id);
    }

    // Получаем схему асинхронно
    // если сущность отсутствует создаем BehaviorSubject
    /**
     * Get or create schema subject in reactive storage.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns BehaviorSubject that holds `EntSchema` value.
     */
    private _getSchema$(
        schemaType: SchemaType,
        entityType: string,
        id: string = '',
    ): BehaviorSubject<EntSchema> {
        let existedSchema = this._getExistedSchema$(schemaType, entityType, id);
        if (existedSchema) return existedSchema;
        const root$ = this._getItemRoot(this.storage$, 'schema');
        const path = this._getItemPath(entityType, id);
        root$[schemaType][path] = new BehaviorSubject(null);
        return this._getSchema$(schemaType, entityType, id);
    }

    // Получаем схему асинхронно
    // если сущность отсутствует создаем BehaviorSubject
    /**
     * Get or create parent-derived schema subject in reactive storage.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type string.
     * @param parentId - Parent entity id string.
     * @returns BehaviorSubject that holds `EntSchema` value.
     */
    private _getSchemaFromParent$(
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
    ): BehaviorSubject<EntSchema> {
        let existedSchema = this._getExistedSchemaFromParent$(
            schemaType,
            entityType,
            parentType,
            parentId,
        );
        if (existedSchema) return existedSchema;
        const root$ = this._getItemRoot(this.storage$, 'schema');
        const path = this._getItemSchemaFromParentPath(entityType, parentType, parentId);
        root$[schemaType][path] = new BehaviorSubject(null);
        return this._getSchemaFromParent$(schemaType, entityType, parentType, parentId);
    }

    /**
     * Get or create list subject in reactive storage.
     *
     * @param entityType - Entity type string.
     * @returns BehaviorSubject that holds `EntList` value.
     */
    private _getList$(entityType: string): BehaviorSubject<EntList> {
        let existedItem = this._getExistedList$(entityType);
        if (existedItem) return existedItem;
        const root$ = this._getItemRoot(this.storage$, 'list');

        const path = this._getItemPath(entityType);
        root$[path] = new BehaviorSubject(null);
        return this._getList$(entityType);
    }

    /**
     * Delete item from both value storage and reactive storage by path.
     * Completes the subject before deleting it.
     *
     * @param storageType - Storage section: `list`, `entity`, or `schema`.
     * @param path - Internal storage key path.
     * @param schemaType - Schema type when deleting schema items. Default: empty string.
     * @returns void
     */
    private _checkAndDeleteStorageItem(storageType, path, schemaType = '') {
        const root$ = this._getItemRoot(this.storage$, storageType, schemaType);
        const root = this._getItemRoot(this.storage, storageType, schemaType);
        if (!root$[path]) return;
        root$[path]?.complete();
        delete root$[path];
        delete root[path];
    }

    /**
     * Build internal storage path for entity/list items.
     *
     * @param entityType - Entity type string.
     * @param id - Optional entity id string. Default: empty string.
     * @returns Path string (`entityType-id` or `entityType`).
     */
    private _getItemPath(entityType: string = '', id: string = '') {
        return id ? `${entityType}-${id}` : entityType;
    }

    /**
     * Build internal storage path for schemas derived from a parent.
     *
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type string.
     * @param parentId - Parent entity id string.
     * @returns Path string.
     */
    private _getItemSchemaFromParentPath(entityType: string, parentType: string, parentId: string) {
        return `${parentType}-${parentId}-${entityType}`;
    }

    /**
     * Get a root object within storage structure.
     *
     * @param from - Storage object (`storage` or `storage$`).
     * @param type - Root type: `list`, `entity`, or `schema`.
     * @param schemaType - Optional schema type when accessing schema root. Default: empty string.
     * @returns Root object reference.
     */
    private _getItemRoot<T, U, S>(
        from: EntStorage<T, U, S>,
        type: string = '',
        schemaType: string = '',
    ): T {
        return schemaType ? from[type][schemaType] : from[type];
    }

    /**
     * Get existing entity item/subject by type + id from a storage structure.
     *
     * @param from - Storage object (`storage` or `storage$`).
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @returns Stored item/subject or `undefined`.
     */
    private _getExistedItem<T, U, S>(
        from: EntStorage<T, U, S>,
        entityType: string = '',
        id: string = '',
    ): T {
        return from.entity[this._getItemPath(entityType, id)];
    }

    /**
     * Get existing schema item/subject by schema type + entity type + id.
     *
     * @param from - Storage object (`storage` or `storage$`).
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Entity id string.
     * @returns Stored schema item/subject or `undefined`.
     */
    private _getExistedSchema<T, U, S>(
        from: EntStorage<T, U, S>,
        schemaType: SchemaType,
        entityType: string = '',
        id: string = '',
    ): S {
        return from.schema[schemaType][this._getItemPath(entityType, id)];
    }

    /**
     * Get existing parent-derived schema item/subject.
     *
     * @param from - Storage object (`storage` or `storage$`).
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type.
     * @param parentId - Parent entity id.
     * @returns Stored schema item/subject or `undefined`.
     */
    private _getExistedSchemaFromParent<T, U, S>(
        from: EntStorage<T, U, S>,
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
    ): S {
        return from.schema[schemaType][
            this._getItemSchemaFromParentPath(entityType, parentType, parentId)
        ];
    }

    /**
     * Get existing list item/subject by entity type.
     *
     * @param from - Storage object (`storage` or `storage$`).
     * @param entityType - Entity type string.
     * @returns Stored list item/subject or `undefined`.
     */
    private _getExistedList<T, U, S>(from: EntStorage<T, U, S>, entityType: string = ''): U {
        let path = this._getItemPath(entityType);
        let existedItem = from.list[path];
        return existedItem;
    }

    // существует ли уже behavior BehaviorSubject для сущности, если да,
    // предпологается, что она будет заполнена данными
    /**
     * Get existing entity subject if it exists.
     *
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns BehaviorSubject or `undefined`.
     */
    private _getExistedItem$(entityType: string, id: string = ''): BehaviorSubject<EntData> {
        return this._getExistedItem(this.storage$, entityType, id);
    }

    // существует ли уже behavior BehaviorSubject для схемы, если да,
    // предпологается, что она будет заполнена данными
    /**
     * Get existing schema subject if it exists.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param id - Entity id string. Default: empty string.
     * @returns BehaviorSubject or `undefined`.
     */
    private _getExistedSchema$(
        schemaType: SchemaType,
        entityType: string,
        id: string = '',
    ): BehaviorSubject<EntSchema> {
        return this._getExistedSchema(this.storage$, schemaType, entityType, id);
    }

    /**
     * Get existing parent-derived schema subject if it exists.
     *
     * @param schemaType - Schema type.
     * @param entityType - Entity type string.
     * @param parentType - Parent entity type.
     * @param parentId - Parent entity id.
     * @returns BehaviorSubject or `undefined`.
     */
    private _getExistedSchemaFromParent$(
        schemaType: SchemaType,
        entityType: string,
        parentType: string,
        parentId: string,
    ): BehaviorSubject<EntSchema> {
        return this._getExistedSchemaFromParent(
            this.storage$,
            schemaType,
            entityType,
            parentType,
            parentId,
        );
    }

    /**
     * Get existing list subject if it exists.
     *
     * @param entityType - Entity type string.
     * @returns BehaviorSubject or `undefined`.
     */
    private _getExistedList$(entityType: string): BehaviorSubject<EntList> {
        return this._getExistedList(this.storage$, entityType);
    }
}
